/**
 * Capabilities editor — add/remove capability chips inline on the Settings tab.
 * Persists as a simple string[] on SupplierProfile.capabilities via the
 * existing updateProfile handler.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Layers, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import type { SupplierProfile } from '../../lib/api';
import { getCapabilitiesList } from './utils';

interface Props {
  supplier: SupplierProfile;
  canEdit: boolean;
  onUpdateProfile: (data: Partial<SupplierProfile>) => Promise<void>;
}

export function CapabilitiesEditor({ supplier, canEdit, onUpdateProfile }: Props) {
  const current = getCapabilitiesList(supplier);
  const [items, setItems] = useState<string[]>(current);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const persist = async (next: string[]) => {
    setSaving(true);
    try {
      await onUpdateProfile({ capabilities: next } as Partial<SupplierProfile>);
      setItems(next);
    } catch {
      toast.error('Failed to update capabilities');
    } finally {
      setSaving(false);
    }
  };

  const addOne = async () => {
    const value = draft.trim();
    if (!value) return;
    if (items.some(c => c.toLowerCase() === value.toLowerCase())) {
      toast.info('Already added');
      setDraft('');
      return;
    }
    setDraft('');
    await persist([...items, value]);
  };

  const removeOne = async (cap: string) => {
    await persist(items.filter(c => c !== cap));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />Capabilities</CardTitle>
        <CardDescription>What this supplier provides — used to match against commodity / sector searches.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2 min-h-[28px]">
          {items.length === 0 && (
            <span className="text-sm text-muted-foreground">No capabilities yet.</span>
          )}
          {items.map((cap, idx) => (
            // Capabilities are free text and the stored list can repeat a
            // name; keying by name alone collided in React.
            <Badge key={`${cap}-${idx}`} variant="secondary" className="gap-1 pl-2 pr-1">
              {cap}
              {canEdit && (
                <button
                  type="button"
                  className="hover:bg-muted rounded p-0.5"
                  onClick={() => removeOne(cap)}
                  disabled={saving}
                  aria-label={`Remove ${cap}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Add a capability (e.g. Digital Systems)"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOne(); } }}
              disabled={saving}
              className="flex-1"
            />
            <Button onClick={addOne} disabled={saving || !draft.trim()}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
